import { type NextPage } from "next";
import type { SanitySiteSettingsNavigation } from "@ouster/shared";

import { SanityRouteView, type SerializedSanityRoute } from "~/features/sanity/route-view";
import type { SanityPageBuilderRenderSection } from "~/features/sanity/page-builder/renderer";
import {
  SSRSanityPageContextHandler,
  SANITY_PRIVATE_CACHE_CONTROL,
  type ExtractSanityPageProps,
} from "~/features/sanity/page-context.server";
import { serializeSection, stripSectionLive } from "~/features/sanity/page-builder/serialize";
import { CUBE_PAGE_SLUG } from "~/lib/cube/slug";

type CubePageProps = {
  route: SerializedSanityRoute;
  sections: SanityPageBuilderRenderSection[];
  siteSettings: SanitySiteSettingsNavigation | null;
};

export const getServerSideProps = SSRSanityPageContextHandler(async (ctx) => {
  ctx.res.setHeader("Cache-Control", SANITY_PRIVATE_CACHE_CONTROL);

  const routeResult = await ctx.sanity.getRouteDocumentByUri(CUBE_PAGE_SLUG);
  const route = routeResult.res.result;

  if (!route?._id) {
    return { notFound: true };
  }

  const { pageBuilder: _pageBuilder, ...serializedRoute } = route;
  const loaded = await ctx.sanity.loadPageBuilderSections({ docId: route._id });
  // `serializeSection` already redacts the cube whitelist for every caller.
  // Stripping `live` on top of that closes the other route to the same data:
  // a section's live `initial.data` carries the unserialized `sectionContent`.
  const sections = loaded.map(serializeSection).map(stripSectionLive);

  return {
    props: {
      route: serializedRoute,
      sections,
      siteSettings: ctx.siteSettings,
    } satisfies CubePageProps,
  };
});

const CubeGatedCmsPage: NextPage<ExtractSanityPageProps<typeof getServerSideProps>> = (props) => (
  <SanityRouteView route={props.route} sections={props.sections} siteSettings={props.siteSettings} />
);

export default CubeGatedCmsPage;
